import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { BACKEND_URL } from "../lib/api";
import { renderMarkdown } from "../lib/newsMarkdown";
import { ArrowLeft, Share2, Twitter, Facebook, MessageCircle, Link as LinkIcon, Eye, Newspaper } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
}

export default function NewsArticle() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [more, setMore] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr("");
    api.get(`/news/${slug}`)
      .then((r) => alive && setPost(r.data))
      .catch((e) => alive && setErr(e?.response?.status === 404 ? "Story not found." : "Couldn't load this story."))
      .finally(() => alive && setLoading(false));
    api.get("/news", { params: { page_size: 6 } })
      .then((r) => alive && setMore((r.data?.posts || []).filter((p) => p.slug !== slug)))
      .catch(() => {});
    return () => { alive = false; };
  }, [slug]);

  const html = useMemo(() => {
    if (!post) return "";
    if (post.body_html) return post.body_html;
    return renderMarkdown(post.body_md || "");
  }, [post]);

  const shareUrl = `${BACKEND_URL}/api/news/share/${slug}`;
  const shareTitle = post?.title || "Tap In TV News";

  const onShare = async (where) => {
    const enc = encodeURIComponent;
    if (where === "twitter") {
      window.open(`https://twitter.com/intent/tweet?url=${enc(shareUrl)}&text=${enc(shareTitle)}&via=tapintvnetwork`, "_blank", "noopener");
    } else if (where === "facebook") {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`, "_blank", "noopener");
    } else if (where === "sms") {
      window.location.href = `sms:?&body=${enc(shareTitle + " " + shareUrl)}`;
    } else if (where === "copy") {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied — paste it anywhere");
      } catch {
        toast.error("Couldn't copy. Long-press to copy manually.");
      }
    } else if (where === "native" && navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: post?.dek || "", url: shareUrl });
      } catch { /* user cancelled */ }
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0b10] text-white pb-20" data-testid="news-article-page">
      {/* Top breaking bar */}
      <div className="bg-red-600 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-between gap-3 text-xs">
          <Link to="/tap-in-news" className="font-black tracking-widest flex items-center gap-1.5 hover:underline" data-testid="news-back-to-feed">
            <ArrowLeft className="w-3.5 h-3.5" /> TAP IN NEWS
          </Link>
          <span className="opacity-90 hidden sm:inline">{fmtDate(new Date().toISOString())}</span>
        </div>
      </div>

      {loading && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8" data-testid="news-article-loading">
          <div className="h-72 rounded-xl bg-white/5 animate-pulse mb-6" />
          <div className="h-10 w-3/4 rounded bg-white/5 animate-pulse mb-3" />
          <div className="h-4 w-1/2 rounded bg-white/5 animate-pulse" />
        </div>
      )}

      {!loading && err && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20 text-center" data-testid="news-article-error">
          <Newspaper className="w-12 h-12 mx-auto opacity-40 mb-4" />
          <div className="text-xl font-bold mb-2">{err}</div>
          <Link to="/tap-in-news" className="inline-block mt-4 px-4 py-2 bg-tv-accent text-black rounded-lg font-bold">
            Back to all stories
          </Link>
        </div>
      )}

      {!loading && post && (
        <>
          {/* Hero */}
          <header className="max-w-5xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-block px-2.5 py-1 text-[10px] font-black tracking-[0.18em] rounded-sm bg-red-600 text-white">
                {(post.category || "BREAKING NEWS").toUpperCase()}
              </span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">
                {fmtDate(post.published_at || post.created_at)}
              </span>
            </div>

            <h1 data-testid="news-article-title" className="font-display font-black text-3xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] mb-4">
              <span className="bg-tv-accent text-black px-1.5 box-decoration-clone">{post.title}</span>
            </h1>

            {post.dek && (
              <p data-testid="news-article-dek" className="text-lg sm:text-xl text-white/80 leading-relaxed mb-6 max-w-3xl">
                {post.dek}
              </p>
            )}

            <div className="flex items-center justify-between border-y border-white/10 py-3 mb-6 gap-3 flex-wrap">
              <div className="text-sm">
                <div className="font-bold">By {post.author_name || "Tap In Staff"}</div>
                <div className="text-white/50 text-xs flex items-center gap-2">
                  {(post.view_count || 0) > 0 && <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" />{post.view_count.toLocaleString()}</span>}
                </div>
              </div>

              <div className="flex items-center gap-1.5" data-testid="news-share-bar">
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mr-1 hidden sm:inline">Share</span>
                <button onClick={() => onShare("twitter")} data-testid="news-share-twitter" aria-label="Share on Twitter" className="w-8 h-8 rounded-full bg-white/10 hover:bg-tv-accent hover:text-black flex items-center justify-center transition">
                  <Twitter className="w-4 h-4" />
                </button>
                <button onClick={() => onShare("facebook")} data-testid="news-share-facebook" aria-label="Share on Facebook" className="w-8 h-8 rounded-full bg-white/10 hover:bg-tv-accent hover:text-black flex items-center justify-center transition">
                  <Facebook className="w-4 h-4" />
                </button>
                <button onClick={() => onShare("sms")} data-testid="news-share-sms" aria-label="Share via SMS / iMessage" className="w-8 h-8 rounded-full bg-white/10 hover:bg-tv-accent hover:text-black flex items-center justify-center transition">
                  <MessageCircle className="w-4 h-4" />
                </button>
                <button onClick={() => onShare("copy")} data-testid="news-share-copy" aria-label="Copy link" className="w-8 h-8 rounded-full bg-white/10 hover:bg-tv-accent hover:text-black flex items-center justify-center transition">
                  <LinkIcon className="w-4 h-4" />
                </button>
                {typeof navigator !== "undefined" && navigator.share && (
                  <button onClick={() => onShare("native")} data-testid="news-share-native" aria-label="Share" className="w-8 h-8 rounded-full bg-white/10 hover:bg-tv-accent hover:text-black flex items-center justify-center transition">
                    <Share2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {post.hero_image_url && (
              <div className="rounded-2xl overflow-hidden ring-1 ring-white/10 mb-8 bg-black">
                <img src={post.hero_image_url} alt="" className="w-full h-auto" data-testid="news-article-hero" />
              </div>
            )}
          </header>

          {/* Body */}
          <article
            data-testid="news-article-body"
            className="max-w-3xl mx-auto px-4 sm:px-6 prose prose-invert"
            dangerouslySetInnerHTML={{ __html: html }}
          />

          {/* Tags */}
          {(post.tags || []).length > 0 && (
            <div className="max-w-3xl mx-auto px-4 sm:px-6 mt-10 flex flex-wrap gap-2" data-testid="news-article-tags">
              {post.tags.map((t) => (
                <span key={t} className="px-2.5 py-1 rounded-full bg-white/5 ring-1 ring-white/10 text-xs text-white/70">
                  #{t}
                </span>
              ))}
            </div>
          )}

          {/* Bottom share row */}
          <div className="max-w-3xl mx-auto px-4 sm:px-6 mt-10 pt-6 border-t border-white/10 flex items-center justify-between gap-3 flex-wrap" data-testid="news-share-bottom">
            <div className="text-sm font-bold uppercase tracking-[0.2em] text-white/60">Spread the word</div>
            <div className="flex items-center gap-2">
              <button onClick={() => onShare("twitter")} className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-bold flex items-center gap-1.5">
                <Twitter className="w-3.5 h-3.5" /> Tweet
              </button>
              <button onClick={() => onShare("facebook")} className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-bold flex items-center gap-1.5">
                <Facebook className="w-3.5 h-3.5" /> Share
              </button>
              <button onClick={() => onShare("copy")} className="px-3 py-2 rounded-lg bg-tv-accent text-black text-xs font-bold flex items-center gap-1.5">
                <LinkIcon className="w-3.5 h-3.5" /> Copy link
              </button>
            </div>
          </div>

          {/* More stories */}
          {more.length > 0 && (
            <section className="max-w-5xl mx-auto px-4 sm:px-6 mt-16" data-testid="news-more-stories">
              <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold mb-4">More on Tap In News</div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {more.slice(0, 6).map((p) => (
                  <Link
                    key={p.id}
                    to={`/tap-in-news/${p.slug}`}
                    className="group block rounded-lg overflow-hidden bg-black ring-1 ring-white/10 hover:ring-tv-accent/50 transition"
                  >
                    <div className="relative aspect-[16/10] bg-[#1a1a26]">
                      {p.hero_image_url ? (
                        <img src={p.hero_image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-red-900 to-black" />
                      )}
                    </div>
                    <div className="p-3">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold mb-1">{p.category || "BREAKING"}</div>
                      <h3 className="font-bold text-sm leading-snug line-clamp-2 group-hover:text-tv-accent transition-colors">{p.title}</h3>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
